import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import '../../../index.css';
import { slugify } from '../../../utils/slugify';
import updateDB from '../../../utils/updateDB';

function ClubInfo({
  club,
  user,
  editableCourses,
  editPermissions,
  username,
  fetchClub,
  homeGolfClub,
  clubReciprocals = [],
}) {
  const clubCounty = club ? club.club_county : '';
  const [clubGender, setClubGender] = useState(club ? club.gender : '');
  const [clubType, setClubType] = useState(club ? club.club_type : '');
  const [clubFormation, setClubFormation] = useState(
    club ? club.club_formation : '',
  );
  const [previousName, setPreviousName] = useState(
    club ? club.previous_name : '',
  );
  const [isEditMode, setIsEditMode] = useState(false);

  const [lastEditedBy, setLastEditedBy] = useState(
    club && club.last_edited_by
      ? club.last_edited_by[club.last_edited_by.length - 1]
      : '',
  );
  const [lastEditedTime, setLastEditedTime] = useState(
    club && club.last_edited_time
      ? club.last_edited_time[club.last_edited_time.length - 1]
      : '',
  );

  const canEdit = () => {
    if (!user) return false;
    if (editPermissions === 'Advanced' || editPermissions === 'Ambassador')
      return true;
    if (
      (editPermissions === 'Basic' ||
        editPermissions === 'Ambassador' ||
        editPermissions === 'Editor') &&
      club.club_name.includes(homeGolfClub)
    ) {
      return true;
    }
    return false;
  };

  // Edit handling functions for each field
  const handleClubTypeChange = async (e) => {
    const newType = e.target.value;
    setClubType(newType);
    if (isEditMode) {
      await updateDB(
        'golf_clubs',
        { club_type: newType },
        'club_name',
        club.club_name,
      );
      fetchClub(); // refetch the club information
    }
  };

  const handleClubGenderChange = async (e) => {
    const newGender = e.target.value;
    setClubGender(newGender);
    if (isEditMode) {
      await updateDB(
        'golf_clubs',
        { gender: newGender },
        'club_name',
        club.club_name,
      );
      fetchClub(); // refetch the club information
    }
  };

  const handleClubFormationChange = async (e) => {
    const newFormation = parseInt(e.target.value, 10);
    setClubFormation(newFormation);
    if (isEditMode) {
      await updateDB(
        'golf_clubs',
        { club_formation: newFormation },
        'club_name',
        club.club_name,
      );
      fetchClub(); // refetch the club information
    }
  };

  const handlePreviousNameChange = async (e) => {
    const newPreviousName = e.target.value;
    setPreviousName(newPreviousName);
    if (isEditMode) {
      await updateDB(
        'golf_clubs',
        { previous_name: newPreviousName },
        'club_name',
        club.club_name,
      );
      fetchClub(); // refetch the club information
    }
  };

  const domesticReciprocals = clubReciprocals.filter(
    (r) => r.reciprocal_type === 'domestic',
  );
  const internationalReciprocals = clubReciprocals.filter(
    (r) => r.reciprocal_type === 'international',
  );

  const saveAllChanges = async () => {
    if (isEditMode) {
      console.log('saveAllChanges triggered');
      try {
        // Update club type
        await updateDB(
          'golf_clubs',
          { club_type: clubType },
          'club_name',
          club.club_name,
        );
        // Update club gender
        await updateDB(
          'golf_clubs',
          { gender: clubGender },
          'club_name',
          club.club_name,
        );

        // Update club formation
        await updateDB(
          'golf_clubs',
          { club_formation: clubFormation },
          'club_name',
          club.club_name,
        );

        // Update previous name
        await updateDB(
          'golf_clubs',
          { previous_name: previousName },
          'club_name',
          club.club_name,
        );

        // Update logic for last edited by and time
        const current_date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format
        setLastEditedBy(username);
        setLastEditedTime(current_date);

        // Ensure we have an array or start with an empty one for last edited info
        const currentEditedByArray = Array.isArray(club.last_edited_by)
          ? club.last_edited_by
          : [];
        const currentEditedTimeArray = Array.isArray(club.last_edited_time)
          ? club.last_edited_time
          : [];

        // Update last edited by and time
        await updateDB(
          'golf_clubs',
          {
            last_edited_by: [...currentEditedByArray, username],
            last_edited_time: [...currentEditedTimeArray, current_date],
          },
          'club_name',
          club.club_name,
        );

        // Refetch club information after saving all changes
        fetchClub();

        console.log('All changes saved successfully!');
      } catch (error) {
        console.error('Error saving changes:', error);
      }

      setIsEditMode(false); // Exit edit mode
    }
  };

  return (
    <section
      id="clubInformation"
      className="mt-2 w-full max-w-screen xl:flex xl:min-h-0 xl:flex-col xl:h-full"
    >
      <article className="flex h-full w-full min-h-0 flex-col py-6 prose max-w-none prose-sm text-left border border-gray-300 rounded-xl bg-white shadow-lg xl:flex-1">
        <h2 className="text-center truncate px-6">
          Club Information:{' '}
          <span className="text-indigo-700">{club.club_name}</span>
        </h2>
        <div className="mx-auto max-w-3xl flex justify-between gap-x-2 sm:gap-x-10 px-2">
          {isEditMode ? (
            <>
              <button
                onClick={() => setIsEditMode(false)}
                className="w-full py-2 px-4 bg-red-500 hover:bg-red-700 text-white font-semibold rounded-lg mr-2 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={solid('eject')} className="mr-2" />
                Cancel
              </button>
              <button
                onClick={saveAllChanges}
                className="w-full py-2 px-4 bg-green-500 hover:bg-green-700 text-white font-semibold rounded-lg ml-2 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={solid('floppy-disk')} className="mr-2" />
                Save Changes
              </button>
            </>
          ) : canEdit() ? (
            <button
              onClick={() => setIsEditMode(true)}
              className="min-w-[200px] mx-auto py-2 px-4 bg-blue-500 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center"
            >
              <FontAwesomeIcon icon={solid('user-pen')} className="mr-2" />
              Edit Table
            </button>
          ) : null}
        </div>
        {lastEditedBy && lastEditedTime && (
          <p className="text-center text-xs italic px-2">
            Last edited {lastEditedTime}
          </p>
        )}
        <table className="mx-auto text-xs sm:text-sm max-w-xl table-bordered border border-slate-500 border-spacing-1 ">
          <thead className="bg-indigo-600">
            <tr>
              <th className="text-white">Golf Club</th>
              <th className="text-white">{club.club_name}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>Club County</th>
              <td>{clubCounty || 'N/A'}</td>
            </tr>
            <tr>
              <th>Club Type</th>
              <td>
                {isEditMode && canEdit() ? (
                  <select
                    value={clubType || ''}
                    onChange={handleClubTypeChange}
                    className="block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Update Club Type</option>
                    <option value="Members' Golf Club">
                      Members' Golf Club
                    </option>
                    <option value="Municipal Golf Club">
                      Municipal Golf Club
                    </option>
                    <option value="Proprietary Golf Club">
                      Proprietary Golf Club
                    </option>
                  </select>
                ) : (
                  clubType || ''
                )}
              </td>
            </tr>
            <tr>
              <th>Club Gender</th>
              <td className="border px-4 py-2">
                {isEditMode && canEdit() ? (
                  <select
                    value={clubGender || ''}
                    onChange={handleClubGenderChange}
                    className="block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Update Club Gender</option>
                    <option value="Female Only">Female Only</option>
                    <option value="Male Only">Male Only</option>
                    <option value="Mixed Gender">Mixed Gender</option>
                  </select>
                ) : (
                  clubGender || ''
                )}
              </td>
            </tr>
            <tr>
              <th>Club Formation</th>
              <td className="border px-4 py-2">
                {isEditMode && canEdit() ? (
                  <input
                    type="number"
                    value={clubFormation || ''}
                    onChange={handleClubFormationChange}
                    className="block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                ) : (
                  clubFormation || 'N/A'
                )}
              </td>
            </tr>
            <tr>
              <th>Original Club Name</th>
              <td className="border px-4 py-2">
                {isEditMode && canEdit() ? (
                  <input
                    type="text"
                    value={previousName}
                    onChange={handlePreviousNameChange}
                    className="block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                ) : (
                  previousName || 'N/A'
                )}
              </td>
            </tr>
            <tr>
              <th>Course Access</th>
              <td className="border px-4 py-2">
                {club.linked_golf_courses.length > 0
                  ? club.linked_golf_courses.map((course, index) => (
                      <Link
                        key={index}
                        to={`/lists/golf-courses/${slugify(course)}`}
                        className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 mr-2 hover:bg-indigo-600 hover:text-white no-underline"
                      >
                        {course}
                      </Link>
                    ))
                  : 'N/A'}
              </td>
            </tr>
            <tr>
              <th>Domestic reciprocals</th>
              <td className="border px-4 py-2">
                {domesticReciprocals.length > 0
                  ? domesticReciprocals.map((row, index) => (
                      <Link
                        key={`${row.reciprocal_club_name}-${index}`}
                        to={`/lists/golf-clubs/${slugify(row.reciprocal_club_name)}`}
                        className="inline-flex items-center rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 mr-2 mb-1 hover:bg-emerald-700 hover:text-white no-underline"
                      >
                        {row.reciprocal_club_name}
                      </Link>
                    ))
                  : 'N/A'}
              </td>
            </tr>
            <tr>
              <th>International reciprocals</th>
              <td className="border px-4 py-2">
                {internationalReciprocals.length > 0
                  ? internationalReciprocals.map((row, index) => (
                      <Link
                        key={`${row.reciprocal_club_name}-${index}`}
                        to={`/lists/golf-clubs/${slugify(row.reciprocal_club_name)}`}
                        className="inline-flex items-center rounded bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-900 mr-2 mb-1 hover:bg-teal-700 hover:text-white no-underline"
                      >
                        {row.reciprocal_club_name}
                      </Link>
                    ))
                  : 'N/A'}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="min-h-0 flex-1 xl:block" aria-hidden="true" />
      </article>
    </section>
  );
}
export default ClubInfo;
